
import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';

/**
 * Componente de Filtro de Tempo para o Painel Administrativo
 * 
 * Funcionalidades:
 * - Botão único que abre um Popover para seleção de período.
 * - Lista de períodos pré-definidos (Hoje, Últimos 7 dias, etc.).
 * - Calendário duplo para seleção de um intervalo de datas personalizado.
 * - Opção "Máximo" para visualizar dados de todo o período.
 * - O Popover permanece aberto ao selecionar um filtro pré-definido para melhor UX.
 */

interface TimeRangeFilterProps {
  onFilterChange: (startDate: Date | null, endDate: Date | null, filterName: string) => void;
  currentFilter: string;
}

export const TimeRangeFilter = ({ onFilterChange, currentFilter }: TimeRangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>();
  const [isMobile, setIsMobile] = useState(false);

  // Detectar viewport mobile (< 640px)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Opções de filtro pré-definidas com as novas opções solicitadas.
  const filterOptions = [
    { key: 'hoje', label: 'Hoje', getDates: () => ({ start: startOfDay(new Date()), end: endOfDay(new Date()) }) },
    { key: 'ontem', label: 'Ontem', getDates: () => { const d = subDays(new Date(), 1); return { start: startOfDay(d), end: endOfDay(d) } } },
    { key: 'ultimos-7-dias', label: 'Últimos 7 dias', getDates: () => ({ start: startOfDay(subDays(new Date(), 6)), end: endOfDay(new Date()) }) },
    { key: 'ultimos-14-dias', label: 'Últimos 14 dias', getDates: () => ({ start: startOfDay(subDays(new Date(), 13)), end: endOfDay(new Date()) }) },
    { key: 'ultimos-30-dias', label: 'Últimos 30 dias', getDates: () => ({ start: startOfDay(subDays(new Date(), 29)), end: endOfDay(new Date()) }) },
    { key: 'esta-semana', label: 'Esta semana', getDates: () => { const d = new Date(); return { start: startOfWeek(d, { weekStartsOn: 1 }), end: endOfWeek(d, { weekStartsOn: 1 }) } } },
    { key: 'semana-passada', label: 'Semana passada', getDates: () => { const d = subDays(new Date(), 7); return { start: startOfWeek(d, { weekStartsOn: 1 }), end: endOfWeek(d, { weekStartsOn: 1 }) } } },
    { key: 'este-mes', label: 'Este mês', getDates: () => { const d = new Date(); return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) } } },
    { key: 'mes-passado', label: 'Mês passado', getDates: () => { const d = subDays(startOfMonth(new Date()), 1); return { start: startOfMonth(d), end: endOfMonth(d) } } },
    { key: 'maximo', label: 'Máximo', getDates: () => ({ start: null, end: null }) },
  ];

  // Identifica se o filtro atual é personalizado (não está na lista de pré-definidos)
  const isCustom = !filterOptions.some(o => o.label === currentFilter);

  // Efeito para sincronizar o calendário com o filtro ativo
  useEffect(() => {
    if (currentFilter) {
      const option = filterOptions.find(o => o.label === currentFilter);
      if (option) {
        const { start, end } = option.getDates();
        if (start && end) {
          setRange({ from: start, to: end });
        } else {
          setRange(undefined);
        }
      } else if (isCustom && currentFilter.includes(' - ')) {
        // Lógica para preencher o calendário se for um filtro personalizado
        // Esta parte pode ser melhorada se o formato da data for sempre o mesmo
      }
    }
  }, [currentFilter]);

  // Função para aplicar um filtro pré-definido
  const handlePresetFilter = (option: typeof filterOptions[0]) => {
    const { start, end } = option.getDates();
    onFilterChange(start, end, option.label);
    // Não fecha o popover para permitir que o usuário veja a mudança no calendário
  };

  // Função para aplicar o filtro personalizado e fechar o popover
  const handleCustomFilterApply = () => {
    if (range?.from && range?.to) {
      onFilterChange(
        startOfDay(range.from),
        endOfDay(range.to),
        `${format(range.from, 'dd/MM/yy', { locale: ptBR })} - ${format(range.to, 'dd/MM/yy', { locale: ptBR })}`
      );
      setIsOpen(false);
    }
  };

  // Componente de conteúdo reutilizável
  const FilterContent = () => (
    <div className={cn(
      "flex",
      isMobile ? "flex-col" : "flex-row"
    )}>
      {/* Coluna da esquerda com filtros pré-definidos */}
      <div className={cn(
        "flex flex-col space-y-2 p-4",
        isMobile ? "border-b w-full" : "border-r min-w-[180px]",
        "border-success/30 bg-success-subtle/30"
      )}>
        {filterOptions.map((option) => (
          <Button
            key={option.key}
            variant={currentFilter === option.label ? 'default' : 'ghost'}
            className={cn(
              "justify-start rounded-lg transition-colors min-h-[44px] py-3 px-4",
              currentFilter === option.label
                ? "bg-primary hover:bg-brand-soft text-primary-foreground"
                : "hover:bg-success-subtle text-foreground"
            )}
            onClick={() => handlePresetFilter(option)}
          >
            {option.label}
          </Button>
        ))}
        <Button
          variant={isCustom ? 'default' : 'ghost'}
          className={cn(
            "justify-start cursor-default rounded-lg transition-colors min-h-[44px] py-3 px-4",
            isCustom
              ? "bg-primary hover:bg-brand-soft text-primary-foreground"
              : "hover:bg-success-subtle text-foreground"
          )}
        >
          Personalizado
        </Button>
      </div>
      {/* Coluna da direita com o calendário */}
      <div className={cn("p-4", isMobile && "w-full")}>
        <Calendar
          initialFocus
          mode="range"
          numberOfMonths={isMobile ? 1 : 2}
          defaultMonth={range?.from}
          selected={range}
          onSelect={setRange}
          locale={ptBR}
          className={cn(
            "pointer-events-auto",
            isMobile && "w-full"
          )}
        />
        <div className={cn(
          "flex justify-end space-x-2 pt-3 border-t border-success/30 mt-3",
          isMobile && "px-0 pb-0"
        )}>
          <Button
            variant="ghost"
            onClick={() => setIsOpen(false)}
            className="rounded-lg hover:bg-secondary min-h-[44px]"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCustomFilterApply}
            disabled={!range?.from || !range?.to}
            className="bg-primary hover:bg-brand-soft text-primary-foreground rounded-lg min-h-[44px]"
          >
            Atualizar
          </Button>
        </div>
      </div>
    </div>
  );

  // Botão trigger (mesmo para mobile e desktop)
  const TriggerButton = (
    <Button
      variant="outline"
      className="h-11 w-full justify-start rounded-xl border-success/30 text-left font-normal shadow-sm transition-all duration-200 hover:border-success/30 hover:bg-success-subtle hover:shadow-md sm:w-[280px]"
      onClick={() => setIsOpen(true)}
    >
      <CalendarIcon className="mr-2 h-4 w-4 text-success-text" />
      <span>{currentFilter}</span>
      <ChevronDown className="ml-auto h-4 w-4 text-success-text opacity-70" />
    </Button>
  );

  // Renderização condicional: Dialog fullscreen em mobile, Popover em desktop
  if (isMobile) {
    return (
      <>
        {TriggerButton}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-full h-full m-0 p-0 rounded-none">
            <DialogHeader className="p-4 border-b border-success/30 bg-success-subtle/30">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-lg font-semibold text-foreground">
                  Selecionar Período
                </DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8 rounded-full hover:bg-secondary"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="overflow-y-auto flex-1">
              <FilterContent />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop: Popover
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {TriggerButton}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 rounded-2xl shadow-lg border-success/30"
        align="start"
        sideOffset={4}
      >
        <FilterContent />
      </PopoverContent>
    </Popover>
  );
};
